import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  MapPin, Heart, Users, GraduationCap, BadgeCheck, Star,
} from "lucide-react";
import useBranding from "@/hooks/useBranding";

const FAV_KEY = "schoolhub_favs";
function getFavs() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); }
  catch { return []; }
}
function saveFavs(list) {
  localStorage.setItem(FAV_KEY, JSON.stringify(list));
}

/**
 * Horizontal school listing card — image left, details right.
 * Includes: favorite (like) button, Official Admission Partner tag with tooltip,
 * Verified by School badge, Featured yellow ribbon, key stats and Enquire Now.
 */
export default function SchoolListingCard({ school: s, compare, toggleCompare, onEnquire }) {
  const brand = useBranding();
  const brandName = brand?.site_title || "School Dial";
  const [favs, setFavs] = useState(getFavs());
  const [likes, setLikes] = useState(Number(s.likes_count || 0));
  useEffect(() => { setFavs(getFavs()); }, []);
  const isFav = favs.includes(s.id);
  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = isFav ? favs.filter((x) => x !== s.id) : [...favs, s.id];
    setFavs(next);
    saveFavs(next);
    // Optimistic UI update
    setLikes((prev) => Math.max(0, prev + (isFav ? -1 : 1)));
    try {
      const url = isFav ? `/schools/${s.id}/unlike` : `/schools/${s.id}/like`;
      const { data } = await api.post(url);
      if (typeof data?.likes_count === "number") setLikes(data.likes_count);
    } catch {
      // Roll back on error
      setLikes((prev) => Math.max(0, prev + (isFav ? 1 : -1)));
    }
  };

  const url = `/schools/${s.slug || s.id}`;
  const fmt = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

  return (
    <div
      className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
      data-testid={`school-card-${s.id}`}
    >
      {/* Featured yellow ribbon */}
      {s.featured && (
        <div
          className="absolute top-4 -left-9 z-10 rotate-[-30deg] bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 px-10 py-1 text-[10px] font-black tracking-widest uppercase shadow-md pointer-events-none"
          data-testid={`featured-ribbon-${s.id}`}
        >
          ★ Featured
        </div>
      )}

      {/* Like (favorite) button with count */}
      <button
        onClick={toggleFav}
        aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
        className={`absolute top-3 right-3 z-10 h-9 rounded-full bg-white/95 backdrop-blur border shadow-sm transition-colors flex items-center gap-1 px-2.5 ${
          isFav ? "border-red-200" : "border-slate-200 hover:border-red-200"
        }`}
        data-testid={`fav-btn-${s.id}`}
      >
        <Heart
          className={`w-4 h-4 transition-colors ${isFav ? "fill-red-500 text-red-500" : "text-slate-400 group-hover:text-red-500"}`}
        />
        <span className={`text-xs font-bold tabular-nums ${isFav ? "text-red-600" : "text-slate-700"}`} data-testid={`fav-count-${s.id}`}>
          {likes}
        </span>
      </button>

      <div className="flex flex-col md:flex-row">
        {/* Feature image — LEFT */}
        <Link to={url} className="md:w-[36%] shrink-0 bg-slate-100 block">
          <div className="relative aspect-[16/10] md:aspect-auto md:h-full min-h-[220px]">
            <img
              src={s.feature_image_url || s.logo_url}
              alt={s.name}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-full h-full object-cover"
            />
            {/* Verified by {SiteTitle} — overlay on featured image only */}
            {s.verified && (
              <div
                className="absolute top-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[11px] font-bold text-emerald-700 border border-emerald-200 shadow-sm"
                data-testid={`verified-badge-${s.id}`}
              >
                {brand?.site_logo_url ? (
                  <img
                    src={brand.site_logo_url}
                    alt={brandName}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="h-4 w-auto max-w-[60px] object-contain"
                  />
                ) : (
                  <GraduationCap className="w-3.5 h-3.5 text-amber-500" />
                )}
                <span>Verified by School</span>
              </div>
            )}
            {s.logo_url && (
              <img
                src={s.logo_url}
                alt=""
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="absolute bottom-3 left-3 w-14 h-14 rounded-lg bg-white border border-slate-200 object-contain p-1 shadow-md"
              />
            )}
          </div>
        </Link>

        {/* Details — RIGHT */}
        <div className="p-5 md:p-6 flex-1 flex flex-col">
          {/* Row 1: name + verified */}
          <div className="flex items-start justify-between gap-3 pr-24">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={url}>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 hover:text-blue-700 leading-tight">
                    {s.name}
                  </h3>
                </Link>
                {s.code && (
                  <span
                    className="inline-block text-[10px] font-mono font-bold tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                    data-testid={`school-code-${s.id}`}
                    title="School code"
                  >
                    {s.code}
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap text-sm text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {[s.locality, s.city, s.state].filter(Boolean).join(", ")}
                </span>
                {s.map_url && (
                  <a
                    href={s.map_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-700 hover:underline text-xs"
                    data-testid={`geo-link-${s.id}`}
                  >
                    View on map ↗
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: tags */}
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {s.admission_open ? (
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">Admission Open</Badge>
            ) : (
              <Badge variant="outline" className="border-slate-300 text-slate-500">Admission Closed</Badge>
            )}
            {s.admission_partner && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-800 px-2.5 py-1 text-[11px] font-bold cursor-help"
                      data-testid={`partner-badge-${s.id}`}
                    >
                      <BadgeCheck className="w-3.5 h-3.5 text-blue-600" />
                      Official Admission Partner
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                    This tag confirms that <b>{brandName}</b> is an official admission partner of this school.
                    You can explore the school and submit your inquiry on <b>{brandName}</b>. Your inquiry goes
                    directly to the school and they will connect with you for the admission.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Row 3: key stats grid */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
            <Stat icon={<GraduationCap className="w-3.5 h-3.5" />} label="Board" value={s.board || "—"} />
            <Stat icon={<Star className="w-3.5 h-3.5" />} label="Classes" value={s.classes_offered || "—"} />
            <Stat icon={<Users className="w-3.5 h-3.5" />} label="Student : Faculty" value={s.student_faculty_ratio || "—"} />
            <Stat
              icon={<span className="text-[13px] font-bold leading-none">₹</span>}
              label="Monthly Fee (Nursery)"
              value={fmt(s.monthly_fee_nursery)}
              highlight
            />
          </div>

          {/* Row 4: actions */}
          <div className="mt-5 flex items-center justify-between gap-3 pt-4 border-t border-slate-100 flex-wrap">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <Checkbox
                checked={compare.includes(s.id)}
                onCheckedChange={() => toggleCompare(s.id)}
                data-testid={`compare-check-${s.id}`}
              />
              Compare
            </label>
            <div className="flex gap-2">
              <Link to={url}>
                <Button size="sm" variant="outline" data-testid={`view-school-${s.id}`}>
                  View Profile
                </Button>
              </Link>
              <Button
                size="sm"
                className="bg-slate-900 hover:bg-slate-800 text-white"
                onClick={() => onEnquire && onEnquire(s)}
                data-testid={`enquire-${s.id}`}
              >
                Enquire Now →
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1">
        <span className="text-slate-500">{icon}</span>
        {label}
      </div>
      <div className={`mt-0.5 font-bold truncate ${highlight ? "text-blue-700" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
